import { useForm, Controller } from "react-hook-form";
import { sendContactEmail } from "@shared/core/communication/email";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useEffect, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { SPACING, TYPOGRAPHY, THEME } from "@shared/constants/designSystem";
import { cn } from "@shared/lib/utils";
import { contactFormSchema } from "@shared/lib/validations/contact";
import { useWebsite, useTurnstile } from "@shared/hooks";
import { EmailFieldWithValidation } from "@shared/components/forms";
import PropTypes from "prop-types";

/**
 * ContactForm component using callback pattern for notifications.
 *
 * Following shared-components best practices to avoid circular dependencies:
 * - This component accepts onSuccess/onError callbacks as props
 * - Parent pages import useToast and pass notification functions down
 * - This prevents circular dependency issues that break Fast Refresh
 *
 * @see shared-components/CLAUDE.md - Toast Notification Patterns
 */
const ContactForm = ({ onSuccess, onError }) => {
  const { websiteId } = useWebsite();
  const { token, setToken, isVerifying, setIsVerifying, resetToken } =
    useTurnstile();
  const turnstileRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(contactFormSchema),
    mode: "onChange",
  });

  const watchedFields = watch();
  const messageLength = watchedFields.message?.length || 0;
  const subjectLength = watchedFields.subject?.length || 0;
  const nameLength = watchedFields.name?.length || 0;

  // Email validation state for enhanced validation
  const [_emailValidation, _setEmailValidation] = useState({
    isValid: false,
    isValidating: false,
  });

  // Only load Turnstile once the user interacts with the form
  const [formTouched, setFormTouched] = useState(false);
  const handleFormFocus = () => {
    if (!formTouched) setFormTouched(true);
  };

  // Cleanup Turnstile widget on unmount to prevent duplicate instances with React StrictMode
  useEffect(() => {
    // Copy ref.current to a local variable to avoid stale closure
    const turnstileWidget = turnstileRef.current;
    return () => {
      if (turnstileWidget) {
        turnstileWidget.remove();
      }
    };
  }, []);

  const onSubmit = async (data) => {
    try {
      // ===== CAPTCHA VERIFICATION =====
      // Check if Turnstile token is available
      if (!token) {
        onError?.(
          "Security verification pending. Please wait a moment and try again.",
        );
        return;
      }

      setIsVerifying(true);

      // Add the contact email from environment
      const contactEmail = import.meta.env.VITE_CONTACT_EMAIL;
      if (!contactEmail) {
        throw new Error("Contact email not configured");
      }

      const result = await sendContactEmail({
        ...data,
        turnstileToken: token, // Include Turnstile token for verification
        contactEmail, // Pass the site-specific contact email
        websiteId, // Add websiteId to the payload
      });

      if (result.success) {
        reset();
        resetToken(); // Clear the Turnstile token after successful submission
        onSuccess?.(
          "Thank you for your message. We will get back to you soon!",
        );
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      // Use custom error message if available, otherwise fallback to generic message
      const errorMessage =
        error.userMessage ||
        "Sorry, there was a problem sending your message. Please try again.";

      onError?.(errorMessage);

      // Log form submission error for debugging (sensitive data already removed by this point)
      console.error("Form submission error:", {
        errorType: error.name,
        timestamp: new Date().toISOString(),
        formId: "contact-form",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const inputClassName = cn(
    "w-full p-3 border rounded-lg",
    "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
    "border-gray-200/50 dark:border-gray-700/50",
    `${THEME.COLORS.TEXT.DEFAULT}`,
    "focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent",
    "focus:bg-white/70 dark:focus:bg-gray-800/70",
    "placeholder:text-gray-500 dark:placeholder:text-gray-400",
    TYPOGRAPHY.BODY.DEFAULT,
    "transition-all duration-200",
  );

  const labelClassName = cn(
    "block mb-2",
    TYPOGRAPHY.BODY.DEFAULT,
    THEME.COLORS.TEXT.DEFAULT,
  );

  const errorClassName = cn(
    `${THEME.COLORS.TEXT.MUTED} ${TYPOGRAPHY.BODY.SMALL}`,
    TYPOGRAPHY.BODY.SMALL,
  );

  const characterCountClassName = cn(
    "text-sm mt-1 text-right",
    THEME.COLORS.TEXT.MUTED,
  );

  const getCharacterCountColor = (current, max) => {
    const percentage = (current / max) * 100;
    if (percentage >= 90) return "text-red-500";
    if (percentage >= 75) return "text-yellow-500";
    return THEME.COLORS.TEXT.MUTED;
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onFocus={handleFormFocus}
      className={`${SPACING.DEFAULT} max-w-2xl mx-auto`}
      aria-label="Contact form"
      noValidate
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="name" className={labelClassName}>
            Name
          </label>
          <input
            type="text"
            id="name"
            className={cn(
              inputClassName,
              errors.name &&
                "border-red-500/70 bg-red-50/30 dark:bg-red-900/10",
              !errors.name &&
                nameLength > 0 &&
                "border-green-500/70 bg-green-50/30 dark:bg-green-900/10",
            )}
            {...register("name")}
            aria-invalid={errors.name ? "true" : "false"}
            aria-describedby={errors.name ? "name-error" : "name-count"}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.name && (
              <p className={errorClassName} role="alert" id="name-error">
                {errors.name.message}
              </p>
            )}
            <p
              className={cn(
                characterCountClassName,
                getCharacterCountColor(nameLength, 50),
              )}
              id="name-count"
            >
              {nameLength}/50
            </p>
          </div>
        </div>

        <div>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <EmailFieldWithValidation
                value={field.value || ""}
                onChange={field.onChange}
                onValidationChange={_setEmailValidation}
                required={true}
                strictMode={true}
                blockDisposable={true}
                showSuggestions={true}
                label="Email"
                placeholder="your@email.com"
                id="email"
                name="email"
                error={errors.email?.message}
              />
            )}
          />
        </div>

        <div>
          <label htmlFor="subject" className={labelClassName}>
            Subject
          </label>
          <input
            type="text"
            id="subject"
            className={cn(
              inputClassName,
              errors.subject &&
                "border-red-500/70 bg-red-50/30 dark:bg-red-900/10",
              !errors.subject &&
                subjectLength > 0 &&
                "border-green-500/70 bg-green-50/30 dark:bg-green-900/10",
            )}
            {...register("subject")}
            aria-invalid={errors.subject ? "true" : "false"}
            aria-describedby={
              errors.subject ? "subject-error" : "subject-count"
            }
          />
          <div className="flex justify-between items-center mt-1">
            {errors.subject && (
              <p className={errorClassName} role="alert" id="subject-error">
                {errors.subject.message}
              </p>
            )}
            <p
              className={cn(
                characterCountClassName,
                getCharacterCountColor(subjectLength, 100),
              )}
              id="subject-count"
            >
              {subjectLength}/100
            </p>
          </div>
        </div>

        <div>
          <label htmlFor="message" className={labelClassName}>
            Message
          </label>
          <textarea
            id="message"
            rows="5"
            className={cn(
              inputClassName,
              errors.message &&
                "border-red-500/70 bg-red-50/30 dark:bg-red-900/10",
              !errors.message &&
                messageLength > 0 &&
                "border-green-500/70 bg-green-50/30 dark:bg-green-900/10",
            )}
            {...register("message")}
            aria-invalid={errors.message ? "true" : "false"}
            aria-describedby={
              errors.message ? "message-error" : "message-count"
            }
          />
          <div className="flex justify-between items-center mt-1">
            {errors.message && (
              <p className={errorClassName} role="alert" id="message-error">
                {errors.message.message}
              </p>
            )}
            <p
              className={cn(
                characterCountClassName,
                getCharacterCountColor(messageLength, 1000),
              )}
              id="message-count"
            >
              {messageLength}/1000
            </p>
          </div>
        </div>

        {/* Cloudflare Turnstile Widget — only loads after user interacts with the form */}
        {formTouched && (
          <Turnstile
            ref={turnstileRef}
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={(token) => {
              setToken(token);
            }}
            onError={(error) => {
              console.error("Turnstile verification error:", error);
              setToken(null);
            }}
            onExpire={() => {
              setToken(null);
            }}
            options={{
              theme: "auto",
              size: "compact",
              action: "contact_form",
            }}
          />
        )}

        <button
          type="submit"
          disabled={isSubmitting || !isValid || !token || isVerifying}
          className={cn(
            "w-full md:w-auto px-6 py-3 rounded-lg",
            "bg-gradient-to-r from-primary-500 to-primary-600",
            "text-white font-medium",
            "backdrop-blur-md",
            "border border-primary-400/20",
            "shadow-lg shadow-primary-500/25",
            "transition-all duration-300",
            "hover:shadow-xl hover:shadow-primary-500/30 hover:scale-[1.02]",
            "hover:from-primary-600 hover:to-primary-700",
            "focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 focus:ring-offset-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg",
            TYPOGRAPHY.BODY.DEFAULT,
          )}
        >
          <span
            className="flex items-center justify-center"
            aria-hidden={isSubmitting || isVerifying}
          >
            {isSubmitting || isVerifying ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isVerifying ? "Verifying..." : "Sending..."}
              </>
            ) : (
              "Send Message"
            )}
          </span>
        </button>

        {/* Cloudflare Turnstile Privacy Notice */}
        <p className={cn("text-xs mt-4 text-center", THEME.COLORS.TEXT.MUTED)}>
          This site is protected by Cloudflare Turnstile. Cloudflare&apos;s{" "}
          <a
            href="https://www.cloudflare.com/privacypolicy/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary-600 dark:hover:text-primary-400"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://www.cloudflare.com/website-terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary-600 dark:hover:text-primary-400"
          >
            Terms of Service
          </a>{" "}
          apply.
        </p>
      </div>
    </form>
  );
};

ContactForm.propTypes = {
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default ContactForm;
